import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { formatDate, formatMoney, formatRelative } from '../ui/format';
import { Badge, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  listProposals,
  proposalTone,
  PROPOSAL_STATUSES,
  type Proposal,
  type ProposalStatus,
} from './api';

/**
 * Every proposal, worst news first.
 *
 * A sent proposal nobody has opened is the row a rep should be calling about; an accepted one is a
 * row somebody else should be onboarding. The status column is the whole screen, and the filters
 * are the questions a sales manager actually asks on a Monday.
 */
export function ProposalsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ProposalStatus | 'ALL'>('ALL');

  const proposals = useQuery({
    queryKey: ['proposals', status],
    queryFn: () => listProposals(status === 'ALL' ? {} : { status: [status] }),
  });

  const columns: Column<Proposal>[] = [
    {
      key: 'number',
      header: '#',
      align: 'end',
      text: (proposal) => `${proposal.number} v${proposal.version}`,
      render: (proposal) => (
        <span className="muted">
          {proposal.number}
          <span className="muted small">{` v${proposal.version}`}</span>
        </span>
      ),
    },
    {
      key: 'title',
      header: t('proposals.title'),
      render: (proposal) => <span className="strong">{proposal.title}</span>,
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      text: (proposal) => t(`proposals.status.${proposal.status}`),
      render: (proposal) => (
        <span className="cell-stack">
          <Badge tone={proposalTone(proposal.status)}>
            {t(`proposals.status.${proposal.status}`)}
          </Badge>
          {proposal.viewedAt && proposal.status === 'VIEWED' && (
            <span className="muted small">{formatRelative(proposal.viewedAt)}</span>
          )}
        </span>
      ),
    },
    {
      key: 'total',
      header: t('proposals.total'),
      align: 'end',
      text: (proposal) => formatMoney(proposal.totalCents),
      render: (proposal) => formatMoney(proposal.totalCents),
    },
    {
      key: 'validUntil',
      header: t('proposals.validUntil'),
      text: (proposal) => (proposal.expiresAt ? formatDate(proposal.expiresAt) : ''),
      render: (proposal) => (
        <span className="muted small">
          {proposal.expiresAt ? formatDate(proposal.expiresAt) : '—'}
        </span>
      ),
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      text: (proposal) => formatDate(proposal.createdAt),
      render: (proposal) => <span className="muted small">{formatDate(proposal.createdAt)}</span>,
    },
    /*
     * Off by default: the two dates that say whether silence is the customer's or ours, and the
     * split between what recurs and what is billed once — the figure a manager wants when the
     * total alone makes two very different deals look the same size.
     */
    {
      key: 'sentAt',
      header: t('proposals.sentAt'),
      hidden: true,
      text: (proposal) => formatDate(proposal.sentAt),
      render: (proposal) => <span className="muted small">{formatDate(proposal.sentAt)}</span>,
    },
    {
      key: 'viewedAt',
      header: t('proposals.viewedAt'),
      hidden: true,
      text: (proposal) => formatDate(proposal.viewedAt),
      render: (proposal) => <span className="muted small">{formatDate(proposal.viewedAt)}</span>,
    },
    {
      key: 'recurring',
      header: t('proposals.recurring'),
      hidden: true,
      align: 'end',
      text: (proposal) => formatMoney(proposal.recurringCents),
      render: (proposal) => formatMoney(proposal.recurringCents),
    },
    {
      key: 'oneOff',
      header: t('proposals.oneOff'),
      hidden: true,
      align: 'end',
      text: (proposal) => formatMoney(proposal.oneOffCents),
      render: (proposal) => formatMoney(proposal.oneOffCents),
    },
  ];

  return (
    <div className="view view--wide">
      <PageHeader title={t('nav.proposals')} description={t('proposals.description')} />

      <div className="toolbar">
        <span className="cell-inline">
          <button
            type="button"
            className={`chip${status === 'ALL' ? ' chip--active' : ''}`}
            onClick={() => setStatus('ALL')}
          >
            {t('proposals.all')}
          </button>
          {PROPOSAL_STATUSES.map((key) => (
            <button
              key={key}
              type="button"
              className={`chip${status === key ? ' chip--active' : ''}`}
              onClick={() => setStatus(key)}
            >
              {t(`proposals.status.${key}`)}
            </button>
          ))}
        </span>
      </div>

      <Panel>
        {/*
          No `sort`: the endpoint takes filters and nothing else, so a sortable header would only
          re-order whatever this page happens to hold rather than the whole list.
        */}
        <DataTable
          tableKey="proposals"
          rows={proposals.data?.proposals}
          columns={columns}
          rowKey={(proposal) => proposal.id}
          isLoading={proposals.isLoading}
          error={proposals.error}
          onRetry={() => void proposals.refetch()}
          onRowClick={(proposal) => navigate(`/vendas/propostas/${proposal.id}`)}
          empty={{ title: t('proposals.none') }}
        />
      </Panel>
    </div>
  );
}
